import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SHIFT_LABELS, COST_ADDER_TYPES, INDUSTRY_FACTORS, INDUSTRY_BY_ID } from '@aisolution/shared';
import { api, type Costing as CostingData, type TravelRow, type MarkupRow, type RepriceSummary } from '../api/client';
import { parsePriceSheet } from '../lib/prices';
import { Hint, HINTS } from '../components/Hint';
import { WageRatesCard } from './WageRatesCard';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

const money = (n: number | null | undefined) => n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const WAGE_TYPES = [
  { v: 'open', label: 'Open shop' }, { v: 'ca_prevailing', label: 'CA prevailing (DIR)' },
  { v: 'nv_prevailing', label: 'NV prevailing' }, { v: 'davis_bacon', label: 'Davis-Bacon (federal)' },
];

// Phase 6 — pricing, labor/wage & costs. Internal-only estimate inputs that feed
// the sell-price preview. Proposals never show this breakdown.
export function Costing() {
  const { t } = useLang();
  const { id = '' } = useParams();
  const [cost, setCost] = useState<CostingData | null>(null);
  const [travel, setTravel] = useState<TravelRow[]>([]);
  const [markups, setMarkups] = useState<MarkupRow[]>([]);
  const [markupDefaults, setMarkupDefaults] = useState<{ name: string; pct: number }[]>([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [reprice, setReprice] = useState<RepriceSummary | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      const [c, t, m] = await Promise.all([api.getCosting(id), api.getTravel(id), api.getMarkups(id)]);
      setCost(c); setTravel(t.travel); setMarkups(m.markups); setMarkupDefaults(m.defaults);
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load', fil: 'Hindi ma-load' })); }
  }
  useEffect(() => { void load(); }, [id]);

  async function saveWage(patch: Record<string, string>) {
    try { await api.saveWageSettings(id, patch); await load(); } catch (e) { setError(`${e}`); }
  }
  async function doReprice() {
    setError(''); setMsg(t({ en: 'Repricing from the hierarchy…', fil: 'Nire-reprice mula sa hierarchy…' }));
    try { const r = await api.repriceBom(id); setReprice(r.summary); await load(); setMsg(''); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Reprice failed', fil: 'Nabigo ang reprice' })); setMsg(''); }
  }
  async function onPriceFile(file: File) {
    setError(''); setMsg(t({ en: `Reading ${file.name}…`, fil: `Binabasa ang ${file.name}…` }));
    try {
      const rows = await parsePriceSheet(file);
      const res = await api.uploadPrices(id, rows);
      setMsg(t({ en: `Imported ${res.inserted} prices${res.skipped ? `, skipped ${res.skipped}` : ''}. Click Reprice to apply.`, fil: `Na-import ang ${res.inserted} na presyo${res.skipped ? `, ni-skip ang ${res.skipped}` : ''}. I-click ang Reprice para ilapat.` }));
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Price import failed', fil: 'Nabigo ang price import' })); setMsg(''); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  }

  const c = cost;
  return (
    <div>
      <Link to={`/projects/${id}/bom`} className="muted" style={{ fontSize: 13 }}>← {t({ en: 'Internal BOM', fil: 'Internal BOM' })}</Link>
      <h1 style={{ margin: '4px 0 0' }}>{t({ en: 'Pricing, labor & costs', fil: 'Pricing, labor at mga gastos' })}</h1>
      <div className="banner" style={{ marginTop: 8 }}>
        <strong>{t({ en: 'Internal only.', fil: 'Internal lang.' })}</strong> {t({ en: 'Wage rates are SAMPLE placeholders — verify against the live CA DIR / DOL determination. Material defaults to internet-estimated until distributor pricing is uploaded.', fil: 'Ang wage rates ay SAMPLE na placeholder — i-verify laban sa live na CA DIR / DOL determination. Ang material ay default na internet-estimated hangga\'t walang na-upload na distributor pricing.' })}
      </div>
      {error && <div className="error">{error}</div>}
      {msg && <div className="banner">{msg}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {/* Wage settings */}
        <div className="card">
          <h2>{t({ en: 'Labor & wage basis', fil: 'Labor at wage basis' })}</h2>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Labeled label={t({ en: 'Wage type', fil: 'Wage type' })}>
              <select value={c?.wage.type ?? 'open'} onChange={(e) => saveWage({ wage_type: e.target.value })}>
                {WAGE_TYPES.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
              </select>
            </Labeled>
            <Labeled label={t({ en: 'Shift', fil: 'Shift' })}>
              <select value={c?.wage.shift ?? 'straight'} onChange={(e) => saveWage({ shift: e.target.value })}>
                {Object.entries(SHIFT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Labeled>
            <Labeled label={t({ en: 'State', fil: 'State' })}><input defaultValue={c?.wage.state ?? ''} onBlur={(e) => saveWage({ state: e.target.value })} placeholder="CA / NV / ID" /></Labeled>
            <Labeled label={t({ en: 'County', fil: 'County' })}><input defaultValue={c?.wage.county ?? ''} onBlur={(e) => saveWage({ county: e.target.value })} placeholder={t({ en: 'e.g. Clark', fil: 'hal. Clark' })} /></Labeled>
          </div>
          {c && c.by_category.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead><tr><th>Classification</th><th>{t({ en: 'Hours', fil: 'Oras' })}</th><th>{t({ en: 'Rate', fil: 'Rate' })}</th><th>{t({ en: 'Labor cost', fil: 'Labor cost' })}</th></tr></thead>
              <tbody>
                {c.by_category.map((b) => (
                  <tr key={b.category}>
                    <td>{b.classification}{b.is_sample && <Hint text={HINTS.sample_wage}><span className="pill pill-needs_review" style={{ marginLeft: 6 }}>{t({ en: 'sample', fil: 'sample' })}</span></Hint>}</td>
                    <td>{b.hours.toFixed(2)}</td><td>{money(b.rate)}/hr</td><td>{money(b.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pricing */}
        <div className="card">
          <h2>{t({ en: 'Material pricing', fil: 'Material pricing' })}</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            {t({ en: 'Hierarchy: approved quote → distributor → admin DB → internet → historical. Upload a distributor price list or supplier quote, then reprice.', fil: 'Hierarchy: approved quote → distributor → admin DB → internet → historical. Mag-upload ng distributor price list o supplier quote, tapos i-reprice.' })}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={doReprice}>{t({ en: 'Reprice BOM', fil: 'Reprice BOM' })}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>{t({ en: 'Upload price list (.xlsx)', fil: 'Mag-upload ng price list (.xlsx)' })}</button>
            <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPriceFile(f); }} />
          </div>
          {reprice && (
            <p className="muted" style={{ marginBottom: 0 }}>
              {t({ en: `Repriced ${reprice.repriced} line(s) off internet; ${reprice.still_internet} still internet-estimated${reprice.stale ? `; ${reprice.stale} stale` : ''}.`, fil: `Na-reprice ang ${reprice.repriced} na line mula sa internet; ${reprice.still_internet} ang internet-estimated pa rin${reprice.stale ? `; ${reprice.stale} ang stale` : ''}.` })}
            </p>
          )}
        </div>
      </div>

      {/* Industry / facility type — sets the labor productivity factor (flywheel: hard-site hours) */}
      {c && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>{t({ en: 'Industry / facility type', fil: 'Industry / facility type' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— {t({ en: 'click your job type to add the right labor hours (badging, escorts, flagging, ICRA…)', fil: 'i-click ang job type mo para maidagdag ang tamang labor hours (badging, escorts, flagging, ICRA…)' })}</span></h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Labeled label={t({ en: "This bid's facility type", fil: 'Facility type ng bid na ito' })}>
              <select
                value={c.basis?.industry ?? 'standard'}
                onChange={(e) => { const f = INDUSTRY_BY_ID[e.target.value]; saveWage({ industry: e.target.value, labor_factor: String(f?.factor ?? 1.0) }); }}
                style={{ minWidth: 320 }}
              >
                {INDUSTRY_FACTORS.map((x) => (
                  <option key={x.id} value={x.id}>{x.label} — +{Math.round((x.factor - 1) * 100)}% {t({ en: 'hours', fil: 'oras' })}</option>
                ))}
              </select>
            </Labeled>
            <div style={{ fontSize: 13, color: 'var(--teal-dark, #0b5)', fontWeight: 700 }}>
              {t({ en: 'Labor factor', fil: 'Labor factor' })}: ×{(INDUSTRY_BY_ID[c.basis?.industry ?? 'standard']?.factor ?? 1).toFixed(2)}
            </div>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
            {INDUSTRY_BY_ID[c.basis?.industry ?? 'standard']?.note}
            {' '}{t({ en: 'Picking a type sets the Labor factor below; you can still fine-tune it. These factors follow standard estimating practice (NECA labor-unit adjustments + MCAA labor factors) — secured/occupied/operational sites take more hours for the same install.', fil: 'Ang pagpili ng type ang magse-set ng Labor factor sa baba; pwede mo pa ring i-fine-tune. Sinusunod ng mga factor na ito ang standard estimating practice (NECA labor-unit adjustments + MCAA labor factors) — ang secured/occupied/operational na sites ay nangangailangan ng mas maraming oras para sa parehong install.' })}
            <strong> {t({ en: 'Re-generate the bid to apply.', fil: 'I-generate ulit ang bid para ilapat.' })}</strong>
          </p>
        </div>
      )}

      {/* Per-bid wage rates (estimator self-service) */}
      {c && <WageRatesCard projectId={id} />}

      {/* Takeoff basis & productivity */}
      {c && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>{t({ en: 'Takeoff basis & productivity', fil: 'Takeoff basis at productivity' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— {t({ en: 'scales cable runs to the building & field labor; re-generate the bid to apply', fil: 'iniaakma ang cable runs sa building at field labor; i-generate ulit ang bid para ilapat' })}</span></h2>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
            <Labeled label={t({ en: 'Building gross SF', fil: 'Building gross SF' })}><input defaultValue={c.basis?.gross_sqft ?? ''} onBlur={(e) => saveWage({ gross_sqft: e.target.value })} placeholder={t({ en: 'e.g. 50000', fil: 'hal. 50000' })} /></Labeled>
            <Labeled label={t({ en: 'Floors', fil: 'Floors' })}><input defaultValue={c.basis?.floors ?? ''} onBlur={(e) => saveWage({ floors: e.target.value })} placeholder="1" /></Labeled>
            <Labeled label={t({ en: 'Telecom rooms (MDF+IDF)', fil: 'Telecom rooms (MDF+IDF)' })}><input defaultValue={c.basis?.idf_count ?? ''} onBlur={(e) => saveWage({ idf_count: e.target.value })} placeholder="1" /></Labeled>
            <Labeled label={t({ en: 'Cable waste %', fil: 'Cable waste %' })}><input defaultValue={((c.basis?.waste_pct ?? 0.10) * 100).toFixed(0)} onBlur={(e) => saveWage({ waste_pct: String((Number(e.target.value) || 0) / 100) })} placeholder="10" /></Labeled>
            <Labeled label={t({ en: 'Labor factor (productivity)', fil: 'Labor factor (productivity)' })}><input key={`lf-${c.basis?.labor_factor ?? 1}`} defaultValue={c.basis?.labor_factor ?? 1.0} onBlur={(e) => saveWage({ labor_factor: e.target.value })} placeholder="1.0" /></Labeled>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 8 }}>
            <Labeled label={t({ en: 'Apprentice ratio (0–0.6)', fil: 'Apprentice ratio (0–0.6)' })}><input defaultValue={c.basis?.apprentice_ratio ?? 0} onBlur={(e) => saveWage({ apprentice_ratio: e.target.value })} placeholder="0" /></Labeled>
            <Labeled label={t({ en: 'Apprentice rate (% of JW)', fil: 'Apprentice rate (% of JW)' })}><input defaultValue={((c.basis?.apprentice_rate_pct ?? 0.65) * 100).toFixed(0)} onBlur={(e) => saveWage({ apprentice_rate_pct: String((Number(e.target.value) || 65) / 100) })} placeholder="65" /></Labeled>
            <Labeled label={t({ en: 'Crew blend (effective)', fil: 'Crew blend (effective)' })}><input value={t({ en: `${Math.round((c.crew_factor ?? 1) * 100)}% of JW rate`, fil: `${Math.round((c.crew_factor ?? 1) * 100)}% ng JW rate` })} readOnly disabled /></Labeled>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
            {t({ en: 'Building size + telecom-room count set the average cable run length (vs. a flat 140 ft allowance). Labor factor > 1.0 for tough conditions (high ceilings, occupied/live site, escorted access); < 1.0 for easy open-construction. Crew mix blends in lower-cost apprentice hours.', fil: 'Ang building size + bilang ng telecom-room ang nagtatakda ng average cable run length (kumpara sa flat na 140 ft allowance). Labor factor > 1.0 para sa mahihirap na kondisyon (high ceilings, occupied/live site, escorted access); < 1.0 para sa madaling open-construction. Ang crew mix ay nagsasama ng mas mababang-gastos na apprentice hours.' })} <strong>{t({ en: 'Estimators can raise the labor factor but not drop it below the facility-type default. On prevailing-wage jobs honor the published apprentice rates & ratio limits. Changes apply on the next bid generation.', fil: 'Pwedeng itaas ng estimators ang labor factor pero hindi ibaba sa facility-type default. Sa prevailing-wage na trabaho, sundin ang published apprentice rates at ratio limits. Maaaplay ang mga pagbabago sa susunod na bid generation.' })}</strong>
          </p>
        </div>
      )}

      {/* Travel & misc */}
      <TravelCard projectId={id} travel={travel} onChange={load} />

      {/* Markups */}
      <MarkupCard projectId={id} markups={markups} defaults={markupDefaults} onChange={load} />

      {/* Costing summary */}
      {c && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>{t({ en: 'Cost summary', fil: 'Cost summary' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— {t({ en: 'sell-price preview (the estimate is finalized in Phase 7)', fil: 'sell-price preview (ang estimate ay finalize sa Phase 7)' })}</span></h2>
          {c.warnings.map((w, i) => <div key={i} className="error">{w}</div>)}
          {c.risk && c.risk.needs_review > 0 && (
            <div className="banner banner-danger" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 13 }}>
                <strong>{t({ en: `Risk-weighted contingency: ${c.suggested_contingency_pct}% suggested.`, fil: `Risk-weighted contingency: ${c.suggested_contingency_pct}% ang suggested.` })}</strong>{' '}
                {t({ en: `${c.risk.needs_review} of ${c.risk.total} symbols are unresolved (uncertain scope). Current contingency is ${markups.find((m) => m.name === 'contingency')?.pct_or_amount ?? 0}%.`, fil: `${c.risk.needs_review} sa ${c.risk.total} na symbols ang unresolved (uncertain scope). Ang kasalukuyang contingency ay ${markups.find((m) => m.name === 'contingency')?.pct_or_amount ?? 0}%.` })}
              </div>
              <button className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={async () => {
                const rows = markups.map((m) => ({ name: m.name, pct: m.name === 'contingency' ? c.suggested_contingency_pct : m.pct_or_amount }));
                await api.saveMarkups(id, rows); await load();
              }}>{t({ en: `Apply ${c.suggested_contingency_pct}%`, fil: `Ilapat ang ${c.suggested_contingency_pct}%` })}</button>
            </div>
          )}
          <table>
            <tbody>
              <Row label="Material cost" v={money(c.material_cost)} />
              <Row label="Material markup" v={money(c.material_markup)} />
              {c.material_escalation > 0 && <Row label="Material escalation" v={money(c.material_escalation)} />}
              <Row label={`Field labor (${c.labor_hours.toFixed(1)} hrs${c.labor_factor !== 1 ? ` · ${c.labor_factor}× productivity` : ''})`} v={money(c.labor_cost)} />
              <Row label="Engineering & design" v={money(c.engineering)} />
              <Row label="Project mgmt & supervision" v={money(c.pm_supervision)} />
              <Row label="Programming, testing & commissioning" v={money(c.commissioning)} />
              <Row label="Travel & misc" v={money(c.travel_total)} />
              <Row label="Direct cost" v={money(c.direct_cost)} strong />
              <Row label="Overhead" v={money(c.overhead)} />
              <Row label="Profit" v={money(c.profit)} />
              <Row label="Sales tax" v={money(c.sales_tax)} />
              <Row label="Freight" v={money(c.freight)} />
              <Row label="Small tools" v={money(c.small_tools)} />
              <Row label="Contingency" v={money(c.contingency)} />
              <Row label="Bond" v={money(c.bond)} />
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, padding: '12px 14px', background: 'var(--teal-light)', borderRadius: 8 }}>
            <strong style={{ fontSize: 16 }}>{t({ en: 'Sell price (preview)', fil: 'Sell price (preview)' })}</strong>
            <strong style={{ fontSize: 20, color: 'var(--teal-dark)' }}>{money(c.sell_price)}</strong>
          </div>
          {/* Forecasted profit & margin */}
          <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 150, padding: '10px 14px', border: '1px solid var(--gray-100)', borderRadius: 8 }}>
              <div className="muted" style={{ fontSize: 12 }}>{t({ en: 'Total cost', fil: 'Total cost' })}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{money(c.total_cost)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 150, padding: '10px 14px', border: '1px solid var(--gold)', borderRadius: 8, background: 'var(--gold-light)' }}>
              <div className="muted" style={{ fontSize: 12 }}>{t({ en: 'Forecasted profit (markup + profit)', fil: 'Forecasted profit (markup + profit)' })}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--teal-dark)' }}>{money(c.forecast_profit)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 130, padding: '10px 14px', border: '1px solid var(--teal)', borderRadius: 8, background: 'var(--teal)', color: 'var(--white)' }}>
              <div style={{ fontSize: 12, opacity: 0.9 }}>{t({ en: 'Forecasted margin', fil: 'Forecasted margin' })}</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{c.margin_pct.toFixed(1)}%</div>
            </div>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            {t({ en: 'Margin = (material markup + profit) ÷ sell price. Adjust the Material markup and Profit rows above to hit your target margin.', fil: 'Margin = (material markup + profit) ÷ sell price. I-adjust ang Material markup at Profit rows sa itaas para abutin ang target margin mo.' })}
          </p>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Link className="btn btn-primary btn-sm" to={`/projects/${id}/outputs`}>{t({ en: 'Outputs: RFIs, PDFs, estimate →', fil: 'Outputs: RFIs, PDFs, estimate →' })}</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function TravelCard({ projectId, travel, onChange }: { projectId: string; travel: TravelRow[]; onChange: () => void }) {
  const { t } = useLang();
  const [type, setType] = useState(COST_ADDER_TYPES[0]!.type);
  const [qty, setQty] = useState('1');
  const [unit, setUnit] = useState('');
  const [notes, setNotes] = useState('');
  async function add() {
    if (!Number(unit)) return;
    await api.addTravel(projectId, { type, qty: Number(qty) || 0, unit_cost: Number(unit) || 0, notes });
    setQty('1'); setUnit(''); setNotes(''); onChange();
  }
  const total = travel.reduce((a, t) => a + (t.qty ?? 0) * (t.unit_cost ?? 0), 0);
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>{t({ en: 'Travel & misc costs', fil: 'Travel at iba pang gastos' })}</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {COST_ADDER_TYPES.map((x) => <option key={x.type} value={x.type}>{x.label} ({x.unit})</option>)}
        </select>
        <input style={{ width: 70 }} value={qty} onChange={(e) => setQty(e.target.value)} placeholder={t({ en: 'Qty', fil: 'Dami' })} />
        <input style={{ width: 100 }} value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit $" />
        <input style={{ flex: 1, minWidth: 120 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t(G.notes)} />
        <button className="btn btn-ghost btn-sm" onClick={add}>{t(G.add)}</button>
      </div>
      {travel.length > 0 && (
        <table style={{ marginTop: 10 }}>
          <thead><tr><th>{t({ en: 'Type', fil: 'Type' })}</th><th>{t({ en: 'Qty', fil: 'Dami' })}</th><th>Unit $</th><th>Ext</th><th>{t(G.notes)}</th><th></th></tr></thead>
          <tbody>
            {travel.map((t) => (
              <tr key={t.id}>
                <td>{COST_ADDER_TYPES.find((x) => x.type === t.type)?.label ?? t.type}</td>
                <td>{t.qty}</td><td>{money(t.unit_cost)}</td><td>{money((t.qty ?? 0) * (t.unit_cost ?? 0))}</td>
                <td className="muted">{t.notes}</td>
                <td><button className="btn btn-ghost btn-sm" onClick={async () => { await api.deleteTravel(projectId, t.id); onChange(); }}>✕</button></td>
              </tr>
            ))}
            <tr><td colSpan={3}><strong>{t(G.total)}</strong></td><td><strong>{money(total)}</strong></td><td colSpan={2} /></tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

const FLOOR_EXEMPT = new Set(['sales_tax']); // county pass-through — adjustable down

function MarkupCard({ projectId, markups, defaults, onChange }: { projectId: string; markups: MarkupRow[]; defaults: { name: string; pct: number }[]; onChange: () => void }) {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const floor = new Map(defaults.map((d) => [d.name, d.pct]));
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState('');
  const val = (m: MarkupRow) => edit[m.name] ?? String(m.pct_or_amount);
  async function save() {
    const rows = markups.map((m) => ({ name: m.name, pct: Number(edit[m.name] ?? m.pct_or_amount) }));
    const res = await api.saveMarkups(projectId, rows); setEdit({});
    if (res.floored && res.floored.length) setMsg(t({ en: `Raised to the company minimum (estimators can't bid below the default): ${res.floored.map((n) => n.replace(/_/g, ' ')).join(', ')}.`, fil: `Itinaas sa company minimum (hindi pwedeng mag-bid ang estimators sa ibaba ng default): ${res.floored.map((n) => n.replace(/_/g, ' ')).join(', ')}.` }));
    else setMsg('');
    onChange();
  }
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Markups</h2>
        <button className="btn btn-ghost btn-sm" onClick={save}>{t({ en: 'Save markups', fil: 'I-save ang markups' })}</button>
      </div>
      {!isAdmin && <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>{t({ en: 'You can raise any markup, but not below the company minimum (the margin floor). Sales tax can be set to the local rate. Below-default markups are reset to the minimum on save.', fil: 'Pwede mong itaas ang kahit anong markup, pero hindi sa ibaba ng company minimum (ang margin floor). Ang sales tax ay pwedeng i-set sa local rate. Ang mga markup na below-default ay ire-reset sa minimum pag-save.' })}</p>}
      {msg && <div className="banner banner-warning" style={{ marginTop: 8, fontSize: 13 }}>⚠️ {msg}</div>}
      <table style={{ marginTop: 10 }}>
        <thead><tr><th>Markup</th><th>%</th><th>{t({ en: 'Min', fil: 'Min' })}</th><th>{t({ en: 'Basis', fil: 'Basis' })}</th></tr></thead>
        <tbody>
          {markups.map((m) => {
            const fl = floor.get(m.name);
            const below = !isAdmin && fl != null && !FLOOR_EXEMPT.has(m.name) && Number(val(m)) < fl;
            return (
              <tr key={m.name}>
                <td>{m.name.replace(/_/g, ' ')}</td>
                <td><input style={{ width: 80, ...(below ? { borderColor: 'var(--danger)', color: 'var(--danger)' } : {}) }} value={val(m)} onChange={(e) => setEdit((s) => ({ ...s, [m.name]: e.target.value }))} /></td>
                <td className="muted" style={{ fontSize: 12 }}>{isAdmin || FLOOR_EXEMPT.has(m.name) || fl == null ? '—' : `≥ ${fl}%`}</td>
                <td className="muted">{m.basis}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>{label}{children}</label>;
}
function Row({ label, v, strong }: { label: string; v: string; strong?: boolean }) {
  return <tr><td style={strong ? { fontWeight: 700 } : undefined}>{label}</td><td style={{ textAlign: 'right', ...(strong ? { fontWeight: 700 } : {}) }}>{v}</td></tr>;
}
